import React, { useState, useEffect, useRef } from 'react';
import { Shield, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface TwoFactorSetupProps {
  userId?: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ userId, onComplete, onCancel }) => {
  const { setupTwoFactor, verifyAndEnableTwoFactor, user } = useAuth();
  const effectiveUserId = userId || user?.id;
  const [verificationCode, setVerificationCode] = useState('');
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  
  // Track if setup has been initialized
  const setupInitializedRef = useRef(false);

  useEffect(() => {
    const initSetup = async () => {
      if (setupInitializedRef.current || !effectiveUserId) return;

      setLoading(true);
      setError(null);

      try {
        setupInitializedRef.current = true;
        const result = await setupTwoFactor(effectiveUserId);

        if (result.error) {
          setError(result.error.message || 'Failed to set up 2FA');
          setupInitializedRef.current = false;
        } else if (result.secret && result.qrCode) {
          setSecret(result.secret);
          setQrCode(result.qrCode);
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
        setupInitializedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    initSetup();
  }, [setupTwoFactor, effectiveUserId]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleVerify = async () => {
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    if (verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
      setError('Verification code must be 6 digits');
      return;
    }

    if (!effectiveUserId) {
      setError('User ID not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await verifyAndEnableTwoFactor(effectiveUserId, verificationCode);
      if (result.error) {
        setError(result.error.message || 'Failed to verify code');
      } else {
        if (onComplete) onComplete();
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl max-w-md mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
          <Shield size={20} className="text-white" />
        </div>
        <h2 className="text-xl font-semibold text-white">Two-Factor Authentication</h2>
      </div>

      {error && (
        <div className="bg-red-600/20 border border-red-600 rounded-xl p-4 flex items-center gap-3 mb-6">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {step === 'setup' ? (
        <>
          <p className="text-slate-300 mb-6">
            Enhance your account security by setting up two-factor authentication. You'll need an authenticator app like Google Authenticator or Authy.
          </p>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {qrCode && (
                <div className="bg-white p-4 rounded-lg mb-6 mx-auto w-fit">
                  <img src={qrCode} alt="QR Code for 2FA" className="w-48 h-48" />
                </div>
              )}

              {secret && (
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-2">Secret Key</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={secret}
                      readOnly
                      className="flex-1 bg-slate-900/50 text-white px-4 py-3 rounded-l-xl border border-slate-600/50 focus:outline-none"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-3 rounded-r-xl border-y border-r border-slate-600/50 flex items-center gap-2"
                    >
                      {copied ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <p className="text-slate-400 text-sm">
                  1. Install an authenticator app like Google Authenticator or Authy
                </p>
                <p className="text-slate-400 text-sm">
                  2. Scan the QR code or enter the secret key manually
                </p>
                <p className="text-slate-400 text-sm">
                  3. Enter the 6-digit verification code from the app to verify
                </p>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setStep('verify')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20"
                >
                  Continue
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <p className="text-slate-300 mb-6">
            Enter the 6-digit verification code from your authenticator app to verify and enable two-factor authentication.
          </p>

          <div className="mb-6">
            <label className="block text-sm text-slate-400 mb-2">Verification Code</label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
              placeholder="Enter 6-digit code"
              className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              maxLength={6}
              pattern="[0-9]{6}"
            />
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('setup')}
              className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              disabled={loading}
            >
              Back
            </button>
            <button
              onClick={handleVerify}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center gap-2"
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Verify & Enable'
              )}
            </button>
          </div>
        </>
      )}

      {onCancel && (
        <div className="mt-6 text-center">
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white transition-colors"
          >
            Cancel Setup
          </button>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;